import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Button,
  Table,
  Drawer,
  Stack,
  Tabs,
  LoadingOverlay,
  TextInput,
  Center,
  Pagination,
  Image,
  Divider,
} from "@mantine/core";
import { IconSearch, IconEye } from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import { API_BASE_URL } from "../../config/baseUrls";

const ADMIN_API = `${API_BASE_URL}/`;
const getAdminHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const STATUS_COLOR = { PENDING_REVIEW: "orange", APPROVED: "green", REJECTED: "red" };
const TIER_COLOR = { USER: "gray", CREATOR: "blue", OFFICIAL: "grape" };

export default function CommunityManagementReview() {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedHub, setSelectedHub] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const { showSuccess, showError } = useNotification();

  const fetchHubs = async (page = 1) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        status: activeTab,
        page,
        limit: 10,
        ...(search ? { search } : {}),
      });
      const res = await fetch(`${ADMIN_API}hubs?${query}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setHubs(data.data.hubs);
        setPagination({
          page: data.data.pagination.page,
          totalPages: data.data.pagination.totalPages,
        });
      } else {
        showError("Error", data.error?.message || "Failed to load communities.");
      }
    } catch (e) {
      showError("Error", "Network error while loading communities.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHubs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openReview = (hub) => {
    setSelectedHub(hub);
    setReviewOpen(true);
  };

  const closeReview = () => {
    setReviewOpen(false);
    setSelectedHub(null);
  };

  const setStatus = async (status) => {
    if (!selectedHub) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${ADMIN_API}hubs/${selectedHub.id}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess(
          status === "APPROVED" ? "Approved" : "Rejected",
          `${selectedHub.name} is now ${status.replace("_", " ").toLowerCase()}.`,
        );
        closeReview();
        fetchHubs(pagination.page);
      } else {
        showError("Action failed", data.error?.message || "Something went wrong.");
      }
    } catch (e) {
      showError("Error", "Network error while updating this community.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box className="space-y-6">
      <Group justify="space-between">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Community Management
          </Text>
          <Text size="sm" c="dimmed">
            Review self-serve Creator tier communities before they go public. USER and
            admin-created communities are auto-approved and won't appear in the Pending queue.
          </Text>
        </Box>
      </Group>

      <Paper p="sm" radius="md" withBorder>
        <Group grow>
          <TextInput
            placeholder="Search by community name or description..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchHubs(1)}
          />
          <Button variant="light" onClick={() => fetchHubs(1)} w={100}>
            Search
          </Button>
        </Group>
      </Paper>

      <Paper withBorder radius="lg" shadow="sm" pos="relative">
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

        <Tabs value={activeTab} onChange={setActiveTab} color="blue" radius="md">
          <Tabs.List px="md" pt="md">
            <Tabs.Tab value="PENDING_REVIEW">
              <Text size="md" fw={600}>Pending</Text>
            </Tabs.Tab>
            <Tabs.Tab value="APPROVED">
              <Text size="md" fw={600}>Approved</Text>
            </Tabs.Tab>
            <Tabs.Tab value="REJECTED">
              <Text size="md" fw={600}>Rejected</Text>
            </Tabs.Tab>
          </Tabs.List>

          <Box p="md" mih={400}>
            <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover>
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Community</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Creator</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Tier</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Members</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Submitted</Text></Table.Th>
                  <Table.Th><Text size="sm" tt="uppercase" c="dimmed">Status</Text></Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {hubs.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7} ta="center" c="dimmed" py="xl">
                      <Text size="lg">No communities in this queue.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  hubs.map((hub) => (
                    <Table.Tr key={hub.id}>
                      <Table.Td><Text fw={700} size="md">{hub.name}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm">{hub.createdBy?.nickname || "Unknown"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={TIER_COLOR[hub.tier]} variant="light" size="sm">{hub.tier}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{hub.memberCount}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{new Date(hub.createdAt).toLocaleDateString()}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLOR[hub.status]} variant="light" size="md">
                          {hub.status.replace("_", " ")}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          variant="light"
                          size="sm"
                          leftSection={<IconEye size={16} />}
                          onClick={() => openReview(hub)}
                        >
                          Review
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Box>
        </Tabs>

        <Center p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchHubs(p)}
          />
        </Center>
      </Paper>

      {/* REVIEW DRAWER — renders from the row already in local state */}
      <Drawer
        opened={reviewOpen}
        onClose={closeReview}
        position="right"
        size="lg"
        title={<Title order={3} size="h2">Community Review</Title>}
        padding="xl"
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        {selectedHub && (
          <Stack gap="xl">
            <Paper withBorder p="lg" radius="md" bg="gray.0">
              <Group align="start" wrap="nowrap">
                {selectedHub.bannerUrl && (
                  <Image
                    src={selectedHub.bannerUrl}
                    w={80}
                    h={80}
                    radius="md"
                    style={{ border: "1px solid var(--mantine-color-gray-3)" }}
                  />
                )}
                <Box style={{ flex: 1 }}>
                  <Title order={3} size="h2">{selectedHub.name}</Title>
                  <Group gap="xs" mt={4} mb="sm">
                    <Badge color={TIER_COLOR[selectedHub.tier]} variant="dot">{selectedHub.tier}</Badge>
                    <Badge color="gray" variant="light">{selectedHub.category}</Badge>
                  </Group>
                  <Text size="sm" c="dark.7">
                    {selectedHub.description || "No description provided."}
                  </Text>
                </Box>
              </Group>
            </Paper>

            <Box>
              <Title order={4} size="h3" mb="sm">Creator</Title>
              <Paper withBorder p="lg" radius="md">
                <Text fw={600}>{selectedHub.createdBy?.nickname || "Unknown"}</Text>
              </Paper>
            </Box>

            {selectedHub.rules && (
              <Box>
                <Title order={4} size="h3" mb="sm">Rules</Title>
                <Paper withBorder p="lg" radius="md">
                  <Text size="sm">{selectedHub.rules}</Text>
                </Paper>
              </Box>
            )}

            <Group gap="xl">
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Members</Text>
                <Text fw={600}>{selectedHub.memberCount}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Submitted</Text>
                <Text fw={600}>{new Date(selectedHub.createdAt).toLocaleDateString()}</Text>
              </Box>
            </Group>

            {selectedHub.status === "PENDING_REVIEW" && (
              <Box mt="auto" pt="xl" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
                <Divider mb="md" />
                <Group grow>
                  <Button
                    size="xl"
                    variant="light"
                    color="red"
                    loading={actionLoading}
                    onClick={() => setStatus("REJECTED")}
                  >
                    Reject
                  </Button>
                  <Button
                    size="xl"
                    color="green"
                    loading={actionLoading}
                    onClick={() => setStatus("APPROVED")}
                  >
                    Approve
                  </Button>
                </Group>
              </Box>
            )}
          </Stack>
        )}
      </Drawer>
    </Box>
  );
}
